import { useCallback, useEffect, useRef, useState } from 'react';

type UseAutoFocusAndSelectOptions = {
  autoFocus?: boolean;
  autoSelect?: boolean;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  localInputRef: React.RefObject<HTMLInputElement>;
  value?: string | number;
};

export function useAutoFocusAndSelect({
  autoFocus,
  autoSelect,
  onFocus,
  onBlur,
  localInputRef,
  value,
}: UseAutoFocusAndSelectOptions) {
  const [isFocused, setIsFocused] = useState(false);
  const autoFocusTriggered = useRef(false);

  const _onFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      if (autoSelect) {
        localInputRef.current?.select();
      }
      onFocus?.(event);
    },
    [onFocus, localInputRef, autoSelect],
  );

  const _onBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(event);
    },
    [onBlur],
  );

  useEffect(() => {
    if (autoFocus && !autoFocusTriggered.current && localInputRef.current) {
      localInputRef.current.focus();

      if (autoSelect) {
        localInputRef.current.select();
      } else if (value !== undefined) {
        const length = String(value).length;
        localInputRef.current.setSelectionRange(length, length);
      }

      autoFocusTriggered.current = true;
    }
  }, [autoFocus, autoSelect, localInputRef, value]);

  return {
    isFocused,
    _onFocus,
    _onBlur,
  };
}
